import { academyVersionRequest, createVersionedCommandSender } from "../../../shared/academyApi";

export type FactualIncidentCategory =
  "technical" | "accessibility" | "wellbeing" | "integrity_observation" | "safeguarding" | "other";
export type FactualIncidentSeverity = "low" | "normal" | "high" | "urgent";

export type FactualIncidentReceipt = {
  id: string;
  exam_session_id: string;
  candidate_registration_id: string | null;
  candidate_user_id: string | null;
  category: FactualIncidentCategory;
  severity: FactualIncidentSeverity;
  occurred_at: string;
  state: "recorded";
  reported_by: string;
  proctor_assignment_id: string;
  proctor_assignment_version: number;
  authority_snapshot_hash: string;
  schema_version: "d12-factual-incident.v1";
  version: 1;
  finding: false;
  academicOutcome: false;
};

export type FactualIncidentRecord = FactualIncidentReceipt & {
  observable_facts: string;
  source: "human_observation";
  immediate_action: string | null;
  created_at: string;
};

const send = createVersionedCommandSender("d12-factual-incident");

const parseReceipt = (value: unknown): FactualIncidentReceipt => {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error("Factual incident receipt is invalid");
  const row = value as Record<string, unknown>;
  const categories = [
    "technical",
    "accessibility",
    "wellbeing",
    "integrity_observation",
    "safeguarding",
    "other",
  ];
  const severities = ["low", "normal", "high", "urgent"];
  if (
    typeof row.id !== "string" ||
    typeof row.exam_session_id !== "string" ||
    !(row.candidate_registration_id === null || typeof row.candidate_registration_id === "string") ||
    !(row.candidate_user_id === null || typeof row.candidate_user_id === "string") ||
    !categories.includes(String(row.category)) ||
    !severities.includes(String(row.severity)) ||
    typeof row.occurred_at !== "string" ||
    typeof row.reported_by !== "string" ||
    typeof row.proctor_assignment_id !== "string" ||
    !Number.isInteger(row.proctor_assignment_version) ||
    typeof row.authority_snapshot_hash !== "string" ||
    !/^[0-9a-f]{64}$/.test(row.authority_snapshot_hash) ||
    row.state !== "recorded" ||
    row.schema_version !== "d12-factual-incident.v1" ||
    row.version !== 1 ||
    row.finding !== false ||
    row.academicOutcome !== false
  )
    throw new Error("Factual incident receipt is unsupported");
  return row as FactualIncidentReceipt;
};

const parseRecord = (value: unknown): FactualIncidentRecord => {
  const receipt = parseReceipt(value);
  const row = value as Record<string, unknown>;
  if (
    typeof row.observable_facts !== "string" ||
    row.observable_facts.length < 5 ||
    row.source !== "human_observation" ||
    !(row.immediate_action === null || typeof row.immediate_action === "string") ||
    typeof row.created_at !== "string"
  )
    throw new Error("Factual incident record is unsupported");
  return {
    ...receipt,
    observable_facts: row.observable_facts,
    source: "human_observation",
    immediate_action: row.immediate_action as string | null,
    created_at: row.created_at,
  };
};

export async function listFactualIncidents(examSessionId: string, signal?: AbortSignal) {
  const value = await academyVersionRequest<unknown>(
    `v1/proctor/sessions/${encodeURIComponent(examSessionId)}/incidents`,
    undefined,
    "GET",
    true,
    undefined,
    undefined,
    signal,
  );
  if (!Array.isArray(value)) throw new Error("Factual incident response must be a list");
  return value.map(parseRecord);
}

export async function recordFactualIncident(
  examSessionId: string,
  input: {
    candidateUserId?: string;
    category: FactualIncidentCategory;
    severity: FactualIncidentSeverity;
    observableFacts: string;
    source: "human_observation";
    immediateAction?: string;
  },
) {
  return parseReceipt(
    await send<unknown>(`v1/proctor/sessions/${encodeURIComponent(examSessionId)}/incidents`, input),
  );
}
